import { useState, useMemo, useRef, useEffect } from 'react'
import { ArrowLeft, Search, Check, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useCalendarsInfinite } from '@/hooks/useCalendars'
import { useCalendarEventsList } from '@/hooks/useCalendarEventsList'
import { useAssignPlanCalendar } from '@/hooks/usePlanCalendar'

interface CalendarEventPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  isSeasonal?: boolean
  currentCalendarId?: string
  currentEventId?: string
}

export function CalendarEventPickerDialog({
  open,
  onOpenChange,
  planId,
  isSeasonal = false,
  currentCalendarId,
  currentEventId,
}: CalendarEventPickerDialogProps) {
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedCalendar, setSelectedCalendar] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [calendarSearch, setCalendarSearch] = useState('')
  const [eventSearch, setEventSearch] = useState('')

  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const assignMutation = useAssignPlanCalendar(planId, isSeasonal)

  // Step 1: Calendars infinite scroll
  const {
    data: calendarsData,
    isLoading: isLoadingCalendars,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCalendarsInfinite(calendarSearch)

  const calendars = useMemo(
    () => calendarsData?.pages.flatMap(page => page.data) ?? [],
    [calendarsData]
  )

  // Step 2: Events for selected calendar
  const { data: events, isLoading: isLoadingEvents } = useCalendarEventsList(
    selectedCalendar?.id
  )

  const filteredEvents = useMemo(() => {
    if (!events) return []
    if (!eventSearch.trim()) return events
    const lower = eventSearch.toLowerCase()
    return events.filter(e => e.name.toLowerCase().includes(lower))
  }, [events, eventSearch])

  // Infinite scroll observer
  useEffect(() => {
    if (step !== 1) return
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root: scrollRef.current, threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [step, hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleSelectCalendar = (id: string, name: string) => {
    setSelectedCalendar({ id, name })
    setSelectedEventId(null)
    setEventSearch('')
    setStep(2)
  }

  const handleBack = () => {
    setStep(1)
    setSelectedEventId(null)
    setEventSearch('')
  }

  const handleAssign = () => {
    if (!selectedEventId) return
    assignMutation.mutate(selectedEventId, {
      onSuccess: () => {
        handleClose()
      },
    })
  }

  const handleClose = () => {
    onOpenChange(false)
    // Reset state after animation
    setTimeout(() => {
      setStep(1)
      setSelectedCalendar(null)
      setSelectedEventId(null)
      setCalendarSearch('')
      setEventSearch('')
    }, 200)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button
                type="button"
                onClick={handleBack}
                className="cursor-pointer rounded-md p-1 hover:bg-accent"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <DialogTitle className="text-[16px] font-semibold">
              {step === 1
                ? `Select a ${isSeasonal ? 'Seasonal ' : ''}Calendar`
                : 'Select an Event'}
            </DialogTitle>
          </div>
          {step === 2 && selectedCalendar && (
            <p className="mt-1 text-[13px] text-[#6a7282]">
              {selectedCalendar.name}
            </p>
          )}
          <div className="flex items-center gap-2 text-[12px] text-[#6a7282] mt-2">
            <span className={step === 1 ? 'font-semibold text-[#101828]' : ''}>
              1. Calendar
            </span>
            <span>{'>'}</span>
            <span className={step === 2 ? 'font-semibold text-[#101828]' : ''}>
              2. Event
            </span>
          </div>
        </DialogHeader>

        {/* Search input */}
        <div className="border-t border-b px-4 py-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="flex h-9 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
              placeholder={
                step === 1 ? 'Search calendars...' : 'Search events...'
              }
              value={step === 1 ? calendarSearch : eventSearch}
              onChange={e =>
                step === 1
                  ? setCalendarSearch(e.target.value)
                  : setEventSearch(e.target.value)
              }
              autoFocus
            />
          </div>
        </div>

        {/* List content */}
        <div
          ref={scrollRef}
          className="max-h-[320px] min-h-[200px] overflow-y-auto px-2 py-1"
        >
          {step === 1 && (
            <>
              {isLoadingCalendars && calendars.length === 0 && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isLoadingCalendars && calendars.length === 0 && (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No calendars found.
                </div>
              )}

              {calendars.map(cal => {
                const isCurrentlyAssigned = cal.id === currentCalendarId
                return (
                  <button
                    key={cal.id}
                    type="button"
                    className="relative flex w-full cursor-pointer select-none items-center rounded-md py-2.5 pl-9 pr-3 text-sm outline-none hover:bg-accent"
                    onClick={() => handleSelectCalendar(cal.id, cal.name)}
                  >
                    <span className="absolute left-2.5 flex h-4 w-4 items-center justify-center">
                      {isCurrentlyAssigned && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </span>
                    <div className="flex flex-col items-start gap-0.5 min-w-0">
                      <span className="truncate font-medium">{cal.name}</span>
                      {cal.event_count !== undefined && cal.event_count > 0 && (
                        <span className="text-[12px] text-[#6a7282]">
                          {cal.event_count} event
                          {cal.event_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}

              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="h-1" />

              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              {isLoadingEvents && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isLoadingEvents && filteredEvents.length === 0 && (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  {events && events.length === 0
                    ? 'This calendar has no events.'
                    : 'No events match your search.'}
                </div>
              )}

              {!isLoadingEvents &&
                filteredEvents.map(event => {
                  const isSelected = event.id === selectedEventId
                  const isCurrentlyAssigned = event.id === currentEventId
                  return (
                    <button
                      key={event.id}
                      type="button"
                      className={`relative flex w-full cursor-pointer select-none items-center justify-between rounded-md py-2.5 pl-9 pr-3 text-sm outline-none hover:bg-accent ${
                        isSelected ? 'bg-accent' : ''
                      }`}
                      onClick={() => setSelectedEventId(event.id)}
                    >
                      <span className="absolute left-2.5 flex h-4 w-4 items-center justify-center">
                        {(isSelected || isCurrentlyAssigned) && (
                          <Check
                            className={`h-4 w-4 ${
                              isSelected ? 'text-primary' : 'text-[#6a7282]'
                            }`}
                          />
                        )}
                      </span>
                      <span className="truncate font-medium text-left min-w-0">
                        {event.name}
                      </span>
                      <span className="ml-3 shrink-0 rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[11px] font-medium text-[#6a7282]">
                        {event.milestone_count} milestone
                        {event.milestone_count !== 1 ? 's' : ''}
                      </span>
                    </button>
                  )
                })}
            </>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          {step === 2 && (
            <Button
              onClick={handleAssign}
              disabled={!selectedEventId || assignMutation.isPending}
              className="cursor-pointer"
            >
              {assignMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Assign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
